/**
 * client/src/lib/use-persisted-boolean.ts
 *
 * The canonical localStorage-backed boolean UI state (sidebar collapse,
 * section expand/collapse, "don't show again" flags). Centralized per
 * DEVELOPMENT/code-style.md §Centralized Modules — several page-local
 * try/catch read/write copies existed before this home (sidebar, audit
 * expand-storage, …); new call-sites use this hook instead of a sixth copy.
 *
 * Persistence is best-effort: a throwing storage (private mode, quota) falls
 * back to plain in-memory state — the toggle still works, it just doesn't
 * survive a reload. The stored value is read ONCE at mount (no cross-tab or
 * cross-instance sync — two mounted hooks with the same key won't follow each
 * other live; last write wins for the next mount).
 */

import { useState } from "react";

/** Read the persisted value, falling back when unset or storage throws. */
function readStored(key: string, fallback: boolean): boolean {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

/**
 * A boolean state whose value survives reloads via localStorage. `fallback`
 * applies only when nothing is persisted yet.
 */
export function usePersistedBoolean(
  key: string,
  fallback: boolean,
): [boolean, () => void, (next: boolean) => void] {
  const [value, setValue] = useState(() => readStored(key, fallback));
  const persist = (next: boolean): void => {
    try {
      window.localStorage.setItem(key, next ? "1" : "0");
    } catch {
      // Best-effort persistence — the in-memory state still applies.
    }
  };
  const toggle = (): void => {
    setValue((prev) => {
      const next = !prev;
      persist(next);
      return next;
    });
  };
  // Explicit setter (third element) — existing callers destructure only the
  // first two and are byte-unaffected by this addition. Needed by callers
  // (e.g. a controlled CollapsibleSection) that must set a KNOWN target
  // value rather than flip whatever the current value happens to be.
  const set = (next: boolean): void => {
    setValue(next);
    persist(next);
  };
  return [value, toggle, set];
}
